import React, { useState } from 'react';
import { Sparkles, X, FileText, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiSmartIngestModal({ isOpen, onClose, onApplyExtractedData }) {
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);

  if (!isOpen) return null;

  const handleParse = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/parse-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text: inputText })
      });
      if (res.ok) {
        const data = await res.json();
        setExtracted(data);
      }
    } catch (e) {
      console.error('Failed to parse document text:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (extracted) {
      onApplyExtractedData(extracted);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-purple-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm">AI Smart Document Ingestion</h3>
              <p className="text-[11px] text-purple-200">Paste Commercial Invoice / Packing List text to auto-fill</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-purple-200 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {!extracted ? (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                Paste Invoice, RFQ Email, or Packing Slip text:
              </label>
              <textarea
                rows={8}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Example:&#10;Shipper: BioHealth Corp, Frankfurt, Germany&#10;Consignee: Stavan Life Sciences, Mumbai, India&#10;Gross Weight: 280 kg, Net: 250 kg&#10;Packages: 8 cartons&#10;Commodity: Pharmaceutical Cold-Chain Vials (2-8°C)&#10;Invoice #: INV-2024-8841"
                className="w-full p-3 text-xs font-mono border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Our AI model automatically parses parties, weights, dimensions, commodity, and temperature ranges.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 bg-emerald-50 p-2.5 rounded-xl border border-emerald-200">
                <CheckCircle2 className="w-4 h-4" />
                <span>Extracted {Object.keys(extracted).length} logistics fields successfully:</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded-xl border border-gray-200">
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Shipper</span>
                  <span className="font-semibold text-slate-800">{extracted.shipper_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Consignee</span>
                  <span className="font-semibold text-slate-800">{extracted.consignee_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Origin ➔ Dest</span>
                  <span className="font-semibold text-slate-800">{extracted.origin_city || 'Origin'} ➔ {extracted.destination_city || 'Dest'}</span>
                </div>
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Weight / Pieces</span>
                  <span className="font-semibold text-slate-800">{extracted.gross_weight_kg || 0} kg ({extracted.pieces || 0} pcs)</span>
                </div>
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Commodity</span>
                  <span className="font-semibold text-slate-800">{extracted.commodity || 'General Cargo'}</span>
                </div>
                <div>
                  <span className="text-gray-400 block text-[10px] uppercase font-bold">Temperature</span>
                  <span className="font-semibold text-slate-800">{extracted.temperature_range || 'Ambient'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setExtracted(null)}
                className="text-xs text-purple-600 font-semibold hover:underline"
              >
                ← Back to text edit
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 text-slate-700 rounded-xl text-xs font-bold"
          >
            Cancel
          </button>

          {!extracted ? (
            <button
              type="button"
              onClick={handleParse}
              disabled={loading || !inputText.trim()}
              className="flex items-center gap-1.5 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>Extract with AI</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleApply}
              className="flex items-center gap-1.5 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
            >
              <span>Auto-Fill Form Fields</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
